import { useEffect, useState } from 'react';
import { dashboardGateway } from './index';
import type { MetricDefinition } from './types';

export interface MetricDictionaryView {
  /** Null until loaded, or when it could not be loaded: a figure without a definition simply has no tooltip. The figures themselves never depend on it. */
  get: (metricId: string) => MetricDefinition | undefined;
  /** Every metric the dictionary lists as having no authoritative source yet, with the reason. Empty until loaded. */
  unavailable: MetricDefinition[];
}

const EMPTY: MetricDictionaryView = { get: () => undefined, unavailable: [] };

function toView(definitions: MetricDefinition[]): MetricDictionaryView {
  const byId = new Map(definitions.map((d) => [d.metricId, d]));
  return { get: (id) => byId.get(id), unavailable: definitions.filter((d) => d.availability === 'Unavailable') };
}

/** Loads the static metric dictionary once per mount. A failure leaves the page fully usable, only without the explanatory text. */
export function useMetricDictionary(): MetricDictionaryView {
  const [view, setView] = useState<MetricDictionaryView>(EMPTY);

  useEffect(() => {
    const controller = new AbortController();
    dashboardGateway
      .getMetricDictionary(controller.signal)
      .then((definitions) => {
        if (!controller.signal.aborted) setView(toView(definitions));
      })
      .catch(() => {
        // Intentionally silent: explanations are an aid, not part of the figures.
      });
    return () => controller.abort();
  }, []);

  return view;
}
